import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  ArrowLeft, 
  Twitter, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Eye, 
  EyeOff,
  ExternalLink,
  Copy,
  ChevronDown,
  ChevronUp,
  Send,
  Trash2,
  Settings,
  AlertCircle,
  HelpCircle
} from 'lucide-react';

import { apiClient, formatP0Error, resolveProjectIdAsync } from '../lib/api';

export default function Integrations() {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // X Integration State
  const [xStatus, setXStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Form state
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  
  // Test post state
  const [testTweetText, setTestTweetText] = useState('Test depuis AI Marketing Engine ! 🚀');

  // Handle OAuth callback params
  useEffect(() => {
    const xConnected = searchParams.get('x_connected');
    const xError = searchParams.get('x_error');
    
    if (xConnected === 'true') {
      toast.success(t('xConnectedSuccess') || 'Compte X connecté avec succès !');
      // Clear params
      window.history.replaceState({}, '', '/integrations');
    }
    
    if (xError) {
      toast.error(`${t('xConnectionError') || 'Erreur de connexion X'}: ${xError}`);
      window.history.replaceState({}, '', '/integrations');
    }
  }, [searchParams, t]);

  // Fetch X status on mount
  useEffect(() => {
    fetchXStatus();
  }, []);

  const fetchXStatus = async () => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.get('/integrations/x/status');
      setXStatus(response.data);
      
      // Auto-fill redirect URI - use production domain in production
      if (!response.data.redirect_uri) {
        const callbackBase = window.location.host.includes('ai-marketing-engine.com') 
          ? 'https://ai-marketing-engine.com' 
          : window.location.origin;
        setRedirectUri(`${callbackBase}/api/auth/x/callback`);
      } else {
        setRedirectUri(response.data.redirect_uri);
      }
    } catch (error) {
      console.error('Failed to fetch X status:', error);
      toast.error(formatP0Error(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!clientId) {
      toast.error(t('clientIdRequired') || 'Client ID est requis');
      return;
    }

    setSaving(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/integrations/x/credentials', {
        client_id: clientId,
        client_secret: clientSecret || null,
        redirect_uri: redirectUri || null
      });

      if (response.data.success) {
        toast.success(t('credentialsSaved') || 'Credentials sauvegardées !');
        setClientId('');
        setClientSecret('');
        fetchXStatus();
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectX = async () => {
    setConnecting(true);
    try {
      // P0 FIX DEFINITIVE: project_id is REQUIRED for X OAuth start
      // Try to resolve a project ID from storage or backend
      const projectId = await resolveProjectIdAsync(null, apiClient);
      
      if (!projectId) {
        toast.error('Aucun projet sélectionné. Veuillez d\'abord créer ou sélectionner un projet dans le dashboard.');
        setConnecting(false);
        return;
      }
      
      const response = await apiClient.get(`/auth/x/start?project_id=${projectId}`);

      if (response.data.authorization_url) {
        // Redirect to X authorization
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors du démarrage OAuth');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('confirmDisconnectX') || 'Êtes-vous sûr de vouloir déconnecter votre compte X ?')) {
      return;
    }

    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.post('/auth/x/disconnect', {});
      toast.success(t('xDisconnected') || 'Compte X déconnecté');
      fetchXStatus();
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la déconnexion');
    }
  };

  const handleDeleteCredentials = async () => {
    if (!window.confirm(t('confirmDeleteCredentials') || 'Êtes-vous sûr de vouloir supprimer vos credentials X ? Cela déconnectera aussi votre compte.')) {
      return;
    }

    try {
      // P0 HARDENING: Use centralized apiClient
      await apiClient.delete('/integrations/x/credentials');
      toast.success(t('credentialsDeleted') || 'Credentials supprimées');
      setXStatus(null);
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la suppression');
    }
  };

  const handleTestPost = async () => {
    if (!testTweetText.trim()) {
      toast.error(t('tweetTextRequired') || 'Le texte du tweet est requis');
      return;
    }

    setTesting(true);
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.post('/auth/x/test-post', { text: testTweetText });

      if (response.data.success) {
        toast.success(t('tweetPosted') || 'Tweet publié avec succès !');
      }
    } catch (error) {
      toast.error(formatP0Error(error) || 'Erreur lors de la publication');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('copied') || 'Copié !');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold">{t('integrations') || 'Intégrations'}</h1>
              <p className="text-sm text-muted-foreground">
                {t('integrationsDesc') || 'Connectez vos comptes de réseaux sociaux'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* X (Twitter) Integration Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                  <Twitter className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="font-heading">X (Twitter)</CardTitle>
                  <CardDescription>
                    {t('xIntegrationDesc') || 'Publiez automatiquement sur X'}
                  </CardDescription>
                </div>
              </div>
              {xStatus?.is_connected ? (
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {t('connected') || 'Connecté'}
                </Badge>
              ) : xStatus?.has_credentials ? (
                <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {t('notConnected') || 'Non connecté'}
                </Badge>
              ) : (
                <Badge variant="outline">
                  <XCircle className="w-3 h-3 mr-1" />
                  {t('notConfigured') || 'Non configuré'}
                </Badge>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Connected Account Info */}
            {xStatus?.is_connected && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  {t('connectedAs') || 'Connecté en tant que'} <strong>@{xStatus.x_username}</strong>
                  {xStatus.connected_at && (
                    <span className="text-sm ml-2">
                      ({new Date(xStatus.connected_at).toLocaleDateString()})
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Instructions Collapsible */}
            <Collapsible open={showInstructions} onOpenChange={setShowInstructions}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    {t('howToGetCredentials') || 'Comment obtenir vos credentials X'}
                  </span>
                  {showInstructions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4">
                <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    {t('setupSteps') || 'Étapes de configuration'}
                  </h4>
                  <ol className="space-y-3 text-sm">
                    <li className="flex gap-2">
                      <span className="font-bold text-primary">1.</span>
                      <div>
                        <p>{t('step1CreateAccount') || 'Créez un compte développeur X (gratuit)'}</p>
                        <a 
                          href="https://developer.twitter.com/en/portal/dashboard" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          developer.twitter.com <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-primary">2.</span>
                      <div>
                        <p>{t('step2CreateApp') || 'Créez un nouveau Project puis une App'}</p>
                        <p className="text-muted-foreground text-xs">Projects & Apps → Add App</p>
                      </div>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-primary">3.</span>
                      <div>
                        <p>{t('step3ConfigAuth') || 'Configurez "User authentication settings"'}</p>
                        <ul className="text-muted-foreground text-xs mt-1 space-y-1">
                          <li>• App permissions: <strong>Read and write</strong></li>
                          <li>• Type of App: <strong>Web App, Automated App or Bot</strong></li>
                          <li>• Callback URL: <strong className="text-primary break-all">{redirectUri}</strong></li>
                          <li>• Website URL: {window.location.origin}</li>
                        </ul>
                      </div>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-primary">4.</span>
                      <div>
                        <p>{t('step4GetKeys') || 'Récupérez le Client ID et Client Secret'}</p>
                        <p className="text-muted-foreground text-xs">Keys and tokens → OAuth 2.0 Client ID and Client Secret</p>
                      </div>
                    </li>
                  </ol>
                  
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t('callbackNote') || 'Assurez-vous que le Callback URL correspond exactement'}
                    </p>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Credentials Form */}
            {!xStatus?.has_credentials ? (
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">{t('enterCredentials') || 'Entrez vos credentials X'}</h4>
                
                <div className="space-y-2">
                  <Label htmlFor="clientId">Client ID *</Label>
                  <Input
                    id="clientId"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Votre Client ID OAuth 2.0"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientSecret">
                    Client Secret 
                    <span className="text-muted-foreground text-xs ml-1">(optionnel pour public clients)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="clientSecret"
                      type={showSecret ? "text" : "password"}
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder="Votre Client Secret"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="redirectUri">
                    Callback URL
                    <span className="text-muted-foreground text-xs ml-1">(auto-généré)</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="redirectUri"
                      value={redirectUri}
                      onChange={(e) => setRedirectUri(e.target.value)}
                      className="font-mono text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(redirectUri)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('copyCallbackNote') || 'Copiez cette URL dans les paramètres de votre app X'}
                  </p>
                </div>

                <Button 
                  onClick={handleSaveCredentials} 
                  disabled={saving || !clientId}
                  className="w-full"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('saving') || 'Sauvegarde...'}
                    </>
                  ) : (
                    t('saveCredentials') || 'Sauvegarder les credentials'
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Credentials Summary */}
                <div className="p-4 border rounded-lg bg-muted/30">
                  <h4 className="font-medium mb-3">{t('savedCredentials') || 'Credentials sauvegardées'}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Client ID:</span>
                      <span className="font-mono">{xStatus.client_id_masked}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Client Secret:</span>
                      <span className="font-mono">••••••••</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Callback URL:</span>
                      <span className="font-mono text-xs truncate max-w-[200px]">{xStatus.redirect_uri}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setXStatus(prev => ({ ...prev, has_credentials: false }));
                      }}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      {t('updateCredentials') || 'Modifier'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteCredentials}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {t('delete') || 'Supprimer'}
                    </Button>
                  </div>
                </div>

                {/* Connect Button */}
                {!xStatus.is_connected && (
                  <Button 
                    onClick={handleConnectX} 
                    disabled={connecting}
                    className="w-full"
                    size="lg"
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('connecting') || 'Connexion...'}
                      </>
                    ) : (
                      <>
                        <Twitter className="w-4 h-4 mr-2" />
                        {t('connectX') || 'Connecter mon compte X'}
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* Test Post Section */}
            {xStatus?.is_connected && (
              <div className="space-y-4 p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20">
                <h4 className="font-medium flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  {t('testPost') || 'Tester la publication'}
                </h4>
                
                <div className="space-y-2">
                  <Label htmlFor="testTweet">{t('tweetText') || 'Texte du tweet'}</Label>
                  <Input
                    id="testTweet"
                    value={testTweetText}
                    onChange={(e) => setTestTweetText(e.target.value)}
                    placeholder="Votre message de test..."
                    maxLength={280}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {testTweetText.length}/280
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleTestPost}
                    disabled={testing || !testTweetText.trim()}
                    className="flex-1"
                  >
                    {testing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('posting') || 'Publication...'}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {t('publishTest') || 'Publier le test'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDisconnect}
                  >
                    {t('disconnect') || 'Déconnecter'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Coming Soon: Other Platforms */}
        <Card className="opacity-60">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t('comingSoon') || 'Bientôt disponible'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {['LinkedIn', 'Instagram', 'Facebook', 'TikTok'].map((platform) => (
                <div 
                  key={platform}
                  className="p-4 border rounded-lg text-center bg-muted/30"
                >
                  <p className="font-medium text-muted-foreground">{platform}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
